// App.tsx - coquille de la landing : micro-routeur, nav, footer, consentement.
// Analytics (Google) n'est chargé qu'après consentement explicite (CNIL).
// Le <head> (title/canonical/OG/description) est mis à jour par route pour le SEO,
// et le focus est déplacé sur la nouvelle vue à chaque navigation (accessibilité).
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
} from "react";
import { themeVars } from "@hop-resto/ui";
import { Nav } from "./components/Nav";
import { Home } from "./components/Home";
import { MentionsLegales, Confidentialite, CGU, CGV } from "./components/Legal";
import { ConsentBanner } from "./components/ConsentBanner";
import { Footer } from "./components/Footer";
import { useRoute, type Path } from "./lib/router";
import { fetchActiveCityNames, FALLBACK_CITY } from "./lib/villes";
import { fetchPartenaires, type Partenaire } from "./lib/partenaires";
import { SITE_URL } from "./lib/constants";
import {
  enableAnalytics,
  getStoredConsent,
  isAnalyticsEnabled,
  storeConsent,
  type Consent,
} from "./lib/consent";

interface RouteMeta {
  title: string;
  desc: string;
}

const DEFAULT_DESC =
  "Hop Resto rassemble les restaurants et les habitants d'un même territoire. Les gourmets réservent en deux gestes, sans compte ; les restaurants pilotent tout leur service depuis un vrai logiciel de réservation.";

const ROUTE_META: Record<Path, RouteMeta> = {
  "/": {
    title: "Hop Resto - Toutes vos réservations dans votre poche",
    desc: DEFAULT_DESC,
  },
  "/mentions-legales": {
    title: "Mentions légales - Hop Resto",
    desc: "Mentions légales du site Hop Resto : éditeur, hébergement, propriété intellectuelle.",
  },
  "/confidentialite": {
    title: "Politique de confidentialité - Hop Resto",
    desc: "Hop Resto collecte le minimum de données : aucun compte pour chercher une table, coordonnées transmises au seul restaurant choisi, durées de conservation courtes.",
  },
  "/cgu": {
    title: "Conditions générales d'utilisation - Hop Resto",
    desc: "Conditions générales d'utilisation de l'application Hop Resto : rôle de mise en relation, engagements de chacun, disponibilité du service.",
  },
  "/cgv": {
    title: "Conditions générales de vente - Hop Resto",
    desc: "Conditions générales de vente de l'offre Hop Resto pour les restaurateurs : abonnement, durée, résiliation, réversibilité des données.",
  },
};

/** Met à jour le <head> pour la route courante (title, canonical, OG, description). */
function updateHead(path: Path): void {
  const meta = ROUTE_META[path];
  const url = SITE_URL + (path === "/" ? "/" : path);
  document.title = meta.title;

  const set = (selector: string, attr: string, value: string) => {
    const el = document.head.querySelector(selector);
    if (el) el.setAttribute(attr, value);
  };
  set('link[rel="canonical"]', "href", url);
  set('meta[name="description"]', "content", meta.desc);
  set('meta[property="og:url"]', "content", url);
  set('meta[property="og:title"]', "content", meta.title);
  set('meta[property="og:description"]', "content", meta.desc);
}

/**
 * Charte landing : surcharge LOCALE des tokens partagés `@hop-resto/ui` - scope
 * landing uniquement (la PWA restaurateur et l'admin gardent leurs couleurs).
 * Posée en style INLINE, APRÈS `themeVars()` : un token custom-property inline
 * ne peut être surchargé que par un autre inline, jamais par une règle CSS
 * `.hr-landing {}` (l'inline l'emporte toujours dans la cascade). D'où ce bloc
 * plutôt qu'un override dans landing.css.
 * Fond crème réchauffé, dominante orange ; l'indigo est un accent SECONDAIRE
 * (utilisé avec parcimonie, ex. les eyebrows). Le vert reste réservé à la
 * disponibilité (token `--green` inchangé).
 */
const LANDING_THEME = {
  "--cream": "#FFF8F2",
  "--coral": "#FF5A1F",
  "--coral-dark": "#E24A12",
  "--coral-tint": "rgba(255, 90, 31, 0.1)",
  "--indigo": "#4F46E5",
} as CSSProperties;

export function App(): ReactElement {
  const { path, navigate } = useRoute();
  const [cityNames, setCityNames] = useState<string[]>([FALLBACK_CITY]);
  const [partenaires, setPartenaires] = useState<Partenaire[]>([]);
  const [consent, setConsent] = useState<Consent | null>(() => getStoredConsent());

  const routeRef = useRef<HTMLDivElement>(null);
  const firstRender = useRef(true);

  // Villes actives (badge « En direct » + select du formulaire). Ne casse jamais.
  useEffect(() => {
    void fetchActiveCityNames().then(setCityNames);
  }, []);

  // Restaurants partenaires du carrousel d'accueil (cochés par l'admin -
  // docs/rules/vitrine-partenaires-landing.md). Ne casse jamais : en cas
  // d'échec la liste reste vide et la section ne s'affiche pas.
  useEffect(() => {
    void fetchPartenaires().then(setPartenaires);
  }, []);

  // Consentement déjà accordé lors d'une visite précédente → Analytics actif.
  useEffect(() => {
    if (consent === "granted") void enableAnalytics();
  }, [consent]);

  // À chaque route : <head> à jour, et focus déplacé sur la nouvelle vue (SR).
  // (GA4 « enhanced measurement » compte déjà les page_view sur history - pas de
  // page_view manuel ici, pour éviter le double comptage.)
  useEffect(() => {
    updateHead(path);
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    routeRef.current?.focus();
  }, [path]);

  const accept = useCallback(() => {
    storeConsent("granted");
    setConsent("granted");
  }, []);
  const refuse = useCallback(() => {
    storeConsent("denied");
    // Retrait effectif : si Analytics tourne déjà (consentement précédent),
    // seul un rechargement stoppe réellement la mesure.
    if (isAnalyticsEnabled()) {
      window.location.reload();
      return;
    }
    setConsent("denied");
  }, []);
  const manageCookies = useCallback(() => setConsent(null), []);

  // Ancres de l'accueil (« La communauté », « Le logiciel », « L'offre ») :
  // retour à l'accueil PUIS défilement - le court délai laisse React monter la
  // page avant qu'on cherche la cible. Respecte `prefers-reduced-motion`.
  const goSection = useCallback(
    (id: string) => {
      navigate("/");
      window.setTimeout(() => {
        const smooth = !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        document.getElementById(id)?.scrollIntoView({
          behavior: smooth ? "smooth" : "auto",
        });
      }, 60);
    },
    [navigate],
  );

  return (
    <div
      className="hr-landing"
      style={{ ...themeVars(), ...LANDING_THEME, fontFamily: undefined, color: undefined }}
    >
      <Nav navigate={navigate} goSection={goSection} />

      {/* tabIndex=-1 : cible de focus programmatique à chaque navigation. */}
      <div ref={routeRef} tabIndex={-1} style={{ outline: "none" }}>
        {path === "/mentions-legales" ? (
          <MentionsLegales navigate={navigate} />
        ) : path === "/confidentialite" ? (
          <Confidentialite navigate={navigate} />
        ) : path === "/cgu" ? (
          <CGU navigate={navigate} />
        ) : path === "/cgv" ? (
          <CGV navigate={navigate} />
        ) : (
          <Home cityNames={cityNames} partenaires={partenaires} />
        )}
      </div>

      {consent === null && (
        <ConsentBanner onAccept={accept} onRefuse={refuse} navigate={navigate} />
      )}

      <Footer navigate={navigate} onManageCookies={manageCookies} />
    </div>
  );
}
